import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { User, Building2, Save, ArrowLeft, Search } from "lucide-react";
import AsyncCreatableSelect from "react-select/async-creatable";
import Select from "react-select";
import { toast } from "react-hot-toast";
import { clientService } from "../../services/clientService";
import { leadService } from "../../services/leadService";
import { formatSSN, formatEIN } from "../../services/helperService";
import * as constantValues from "../../constants/constants";
import DeletableAsyncOption from "../deletableAsyncOption";

const buildEmptyFormData = () => ({
  // Common fields
  client_type: "",
  name: "",
  filing_status: "",
  email: "",
  secondary_email: "",
  phone: "",
  additional_phones: "",
  contact_method: "",
  status: "",
  address: "",
  city: "",
  country: "",
  international: "",
  sales_rep_id: "",
  sales_rep_name: "",
  client_manager_id: "",
  client_manager_name: "",
  language: "",
  document_url: "",
  // Person fields
  spouse_name: "",
  filing_details: "",
  state_filing: [],
  ssn: "",
  spouse_ssn: "",
  date_birth: "",
  spouse_date_birth: "",
  // Business fields
  dba: "",
  contact_name: "",
  filing_state: [],
  ein: "",
  incorporation_date: "",
  incorporation_state: "",
  accounting_method: "",
  industry: "",
});

const toPrefillText = (value) =>
  value === undefined || value === null ? "" : String(value);

const toPrefillArray = (value) =>
  Array.isArray(value)
    ? value.filter(
        (item) =>
          item !== undefined && item !== null && String(item).trim() !== "",
      )
    : [];

const normalizeLeadPrefillData = (data = {}) => ({
  name: toPrefillText(data.name),
  filing_status: toPrefillText(data.filing_status),
  email: toPrefillText(data.email),
  secondary_email: toPrefillText(data.secondary_email),
  phone: toPrefillText(data.phone),
  additional_phones: toPrefillText(data.additional_phones),
  contact_method: toPrefillText(data.contact_method),
  status: toPrefillText(data.status),
  address: toPrefillText(data.address),
  city: toPrefillText(data.city),
  country: toPrefillText(data.country),
  international: toPrefillText(data.international),
  sales_rep_id: data.sales_rep_id ?? "",
  sales_rep_name: toPrefillText(data.sales_rep_name),
  client_manager_id: data.client_manager_id ?? "",
  client_manager_name: toPrefillText(data.client_manager_name),
  language: toPrefillText(data.language),
  spouse_name: toPrefillText(data.spouse_name),
  filing_details: toPrefillText(data.filing_details),
  state_filing: toPrefillArray(data.state_filing),
  ssn: toPrefillText(data.ssn),
  spouse_ssn: toPrefillText(data.spouse_ssn),
  date_birth: toPrefillText(data.date_birth),
  spouse_date_birth: toPrefillText(data.spouse_date_birth),
  dba: toPrefillText(data.dba),
  contact_name: toPrefillText(data.contact_name),
  filing_state: toPrefillArray(data.filing_state),
  ein: toPrefillText(data.ein),
  incorporation_date: toPrefillText(data.incorporation_date),
  incorporation_state: toPrefillText(data.incorporation_state),
  accounting_method: toPrefillText(data.accounting_method),
  industry: toPrefillText(data.industry),
});

const CreateClient = ({ userPermission, onClientCreated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [clientType, setClientType] = useState("person");
  const [loading, setLoading] = useState(false);
  const [idLookup, setIdLookup] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState("");
  const [formData, setFormData] = useState(() => buildEmptyFormData());
  const [salesRepDefaults, setSalesRepDefaults] = useState([]);
  const [clientManagerDefaults, setClientManagerDefaults] = useState([]);
  const [deletingSalesRepId, setDeletingSalesRepId] = useState(null);
  const [deletingClientManagerId, setDeletingClientManagerId] = useState(null);
  const stateOptions = constantValues.usStateOptions.map((s) => ({
    value: s.value,
    label: s.label,
  }));

  const digitsOnly = useMemo(
    () => (idLookup || "").replace(/\D+/g, ""),
    [idLookup],
  );

  useEffect(() => {
    const prefill = location.state?.leadPrefill;
    if (!prefill) return;

    const resolvedType =
      prefill.clientType === "business" ? "business" : "person";
    const prefillData = normalizeLeadPrefillData(prefill.formData);
    setClientType(resolvedType);
    setFormData((prev) => ({ ...prev, ...prefillData }));
    setIdLookup(prefillData.ssn || prefillData.ein || "");

    const nextState = { ...(location.state || {}) };
    delete nextState.leadPrefill;
    navigate(location.pathname, { replace: true, state: nextState });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (digitsOnly.length !== 9) {
      setSearchResults([]);
      setSearchError("");
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);
    setSearchError("");

    const t = setTimeout(async () => {
      try {
        const results = await clientService.getClients(
          digitsOnly,
          clientType,
          10,
          0,
        );
        if (!cancelled) setSearchResults(results?.items || []);
      } catch (e) {
        if (!cancelled) setSearchError("Couldn’t search right now.");
        console.error("SSN/EIN search error", e);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [digitsOnly, clientType]);

  useEffect(() => {
    (async () => {
      try {
        const [reps, mgrs] = await Promise.all([
          clientService.getSalesReps(""),
          clientService.getClientManagers(""),
        ]);
        setSalesRepDefaults(reps.map((r) => ({ value: r.id, label: r.name })));
        setClientManagerDefaults(
          mgrs.map((m) => ({ value: m.id, label: m.name })),
        );
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const loadSalesRepOptions = async (inputValue) => {
    const rows = await clientService.getSalesReps(inputValue);
    return rows.map((r) => ({ value: r.id, label: r.name }));
  };
  const loadClientManagerOptions = async (inputValue) => {
    const rows = await clientService.getClientManagers(inputValue);
    return rows.map((m) => ({ value: m.id, label: m.name }));
  };

  const debounce = (fn, delay = 300) => {
    let t;
    return (...args) =>
      new Promise((resolve) => {
        clearTimeout(t);
        t = setTimeout(async () => resolve(await fn(...args)), delay);
      });
  };
  const debouncedLoadSales = useMemo(() => debounce(loadSalesRepOptions), []);
  const debouncedLoadManagers = useMemo(
    () => debounce(loadClientManagerOptions),
    [],
  );

  const selectedSalesRepOption = useMemo(() => {
    if (!formData.sales_rep_id) return null;

    const matched = salesRepDefaults.find(
      (option) => String(option.value) === String(formData.sales_rep_id),
    );
    if (matched) return matched;
    if (!formData.sales_rep_name) return null;

    return { value: formData.sales_rep_id, label: formData.sales_rep_name };
  }, [formData.sales_rep_id, formData.sales_rep_name, salesRepDefaults]);

  const salesRepOptions = useMemo(() => {
    if (!selectedSalesRepOption) return salesRepDefaults;

    const selectedExists = salesRepDefaults.some(
      (option) => String(option.value) === String(selectedSalesRepOption.value),
    );
    return selectedExists
      ? salesRepDefaults
      : [selectedSalesRepOption, ...salesRepDefaults];
  }, [selectedSalesRepOption, salesRepDefaults]);

  const selectedClientManagerOption = useMemo(() => {
    if (!formData.client_manager_id) return null;

    const matched = clientManagerDefaults.find(
      (option) => String(option.value) === String(formData.client_manager_id),
    );
    if (matched) return matched;
    if (!formData.client_manager_name) return null;

    return {
      value: formData.client_manager_id,
      label: formData.client_manager_name,
    };
  }, [
    formData.client_manager_id,
    formData.client_manager_name,
    clientManagerDefaults,
  ]);

  const clientManagerOptions = useMemo(() => {
    if (!selectedClientManagerOption) return clientManagerDefaults;

    const selectedExists = clientManagerDefaults.some(
      (option) =>
        String(option.value) === String(selectedClientManagerOption.value),
    );
    return selectedExists
      ? clientManagerDefaults
      : [selectedClientManagerOption, ...clientManagerDefaults];
  }, [selectedClientManagerOption, clientManagerDefaults]);

  const handleDeleteSalesRepOption = useCallback(async (option) => {
    if (!option?.value) return;
    if (
      !window.confirm(
        `Delete sales rep "${option.label}"? This will unassign it from any client or lead.`,
      )
    ) {
      return;
    }
    try {
      setDeletingSalesRepId(option.value);
      await clientService.deleteSalesRep(option.value);
      setSalesRepDefaults((prev) =>
        prev.filter((item) => item.value !== option.value),
      );
      setFormData((prev) => {
        if (prev.sales_rep_id !== option.value) return prev;
        return { ...prev, sales_rep_id: null, sales_rep_name: "" };
      });
      toast.success(`Sales rep "${option.label}" deleted`);
    } catch (error) {
      console.error("Delete sales rep error", error);
      toast.error("Could not delete Sales Rep");
    } finally {
      setDeletingSalesRepId(null);
    }
  }, []);

  const handleDeleteClientManagerOption = useCallback(async (option) => {
    if (!option?.value) return;
    if (
      !window.confirm(
        `Delete client manager "${option.label}"? This will unassign it from any clients.`,
      )
    ) {
      return;
    }
    try {
      setDeletingClientManagerId(option.value);
      await clientService.deleteClientManager(option.value);
      setClientManagerDefaults((prev) =>
        prev.filter((item) => item.value !== option.value),
      );
      setFormData((prev) => {
        if (prev.client_manager_id !== option.value) return prev;
        return { ...prev, client_manager_id: null, client_manager_name: "" };
      });
      toast.success(`Client manager "${option.label}" deleted`);
    } catch (error) {
      console.error("Delete client manager error", error);
      toast.error("Could not delete Client Manager");
    } finally {
      setDeletingClientManagerId(null);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.name) {
      toast.error("Name is required for persons");
      return false;
    }
    if (!formData.filing_status) {
      toast.error("Filing status is required");
      return false;
    }
    if (!formData.email) {
      toast.error("Email is required");
      return false;
    }
    if (!formData.phone) {
      toast.error("Phone number is required");
      return false;
    }
    if (clientType === "person") {
      if (!formData.filing_details) {
        toast.error("Filing details are required");
        return false;
      }
      if (!formData.ssn) {
        toast.error("SSN is required");
        return false;
      }
      if (!formData.date_birth) {
        toast.error("Date of birth is required");
        return false;
      }
    } else {
      if (!formData.contact_name) {
        toast.error("Contact name is required");
        return false;
      }
      if (!formData.ein) {
        toast.error("EIN is required");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare client data based on type
      const convertedFromLeadId = location.state?.convertedFromLeadId ?? null;

      const clientData = {
        ...(convertedFromLeadId !== null && {
          converted_from_lead_id: convertedFromLeadId,
        }),
        client: {
          client_type: clientType,
          name: formData.name,
          filing_status: formData.filing_status,
          email: formData.email,
          secondary_email: formData.secondary_email || null,
          phone: formData.phone,
          additional_phones: formData.additional_phones || null,
          contact_method: formData.contact_method || null,
          status: formData.status || null,
          address: formData.address || null,
          city: formData.city || null,
          country: formData.country || null,
          international: formData.international || null,
          sales_rep_id: formData.sales_rep_id || null,
          sales_rep_name: formData.sales_rep_name || null,
          client_manager_id: formData.client_manager_id || null,
          client_manager_name: formData.client_manager_name || null,
          language: formData.language || null,
        },
        ...(clientType === "person"
          ? {
              person: {
                spouse_name: formData.spouse_name || null,
                filing_details: formData.filing_details,
                state_filing: formData.state_filing || null,
                ssn: formData.ssn,
                spouse_ssn: formData.spouse_ssn || null,
                date_birth: formData.date_birth,
                spouse_date_birth: formData.spouse_date_birth || null,
              },
            }
          : {
              business: {
                dba: formData.dba || null,
                contact_name: formData.contact_name,
                filing_state: formData.filing_state || null,
                ein: formData.ein,
                incorporation_date: formData.incorporation_date || null,
                incorporation_state: formData.incorporation_state || null,
                accounting_method: formData.accounting_method || null,
                industry: formData.industry || null,
              },
            }),
      };

      const newClient = await clientService.createClient(clientData);

      const leadNotes = location.state?.leadNotes;
      const leadQuickbooks = location.state?.leadQuickbooks;
      const leadCredentials = location.state?.leadCredentials;
      const convertedLeadId = location.state?.convertedFromLeadId;

      await Promise.all([
        leadQuickbooks && leadQuickbooks.type
          ? clientService
              .upsertQuickBooks(newClient.id, {
                type: leadQuickbooks.type,
                username: leadQuickbooks.username,
                password: leadQuickbooks.password,
              })
              .catch(console.error)
          : Promise.resolve(),
        ...(Array.isArray(leadCredentials)
          ? leadCredentials.map((c) =>
              clientService
                .createCredential(newClient.id, {
                  entity_name: c.entity_name,
                  entity_link: c.entity_link,
                  username: c.username,
                  password: c.password,
                })
                .catch(console.error),
            )
          : []),
      ]);

      if (Array.isArray(leadNotes) && leadNotes.length > 0) {
        await Promise.all(
          leadNotes.map(async (note) => {
            let asset_url = null;
            if (note.asset_url && convertedLeadId) {
              try {
                const { blob, contentType } = await leadService.getNoteFile(
                  convertedLeadId,
                  note.asset_url,
                );
                const filename =
                  note.asset_url.split("/").pop() || note.title || "attachment";
                const file = new File([blob], filename, { type: contentType });
                const uploaded = await clientService.uploadNoteAttachment(
                  newClient.id,
                  { title: note.title || "Note", author: note.author, file },
                );
                asset_url = uploaded?.path || null;
              } catch {
                // attachment copy failed — note will still be created without the file
              }
            }
            return clientService.addNote(newClient.id, {
              title: note.title,
              content: note.content,
              author: note.author,
              who_updated_last: note.who_updated_last,
              asset_url,
            });
          }),
        );
      }

      toast.success("Client created successfully!");
      onClientCreated(newClient);
      navigate("/clients");
    } catch (error) {
      if (error.message.includes("SharePoint")) {
        toast.error(error.message);
      } else {
        toast.error("Failed to create client");
      }
      console.error("Create client error: ", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate("/clients")}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Clients
        </button>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Add New Client
        </h1>
        <p className="text-gray-600">
          Create a new person or business client profile
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Client Type Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Client Type
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setClientType("person")}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                clientType === "person"
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center mb-2">
                <User className="h-5 w-5 mr-2" />
                <span className="font-medium">Person</span>
              </div>
              <p className="text-sm text-gray-600">
                Individual client or contact
              </p>
            </button>

            <button
              type="button"
              onClick={() => setClientType("business")}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                clientType === "business"
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center mb-2">
                <Building2 className="h-5 w-5 mr-2" />
                <span className="font-medium">Business</span>
              </div>
              <p className="text-sm text-gray-600">Business or organization</p>
            </button>
          </div>
        </div>

        {/* Client Existence */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Client Existence
          </h2>

          <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 w-full">
            <Search className="h-4 w-4 text-canopy-textMuted mr-2" />
            <input
              type="number"
              placeholder={`Search by ${
                clientType === "person" ? "SSN" : "EIN"
              }`}
              value={idLookup}
              onChange={(e) => setIdLookup(e.target.value)}
              maxLength={9}
              className="flex-1 outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
            />
            {idLookup && (
              <button
                onClick={() => setIdLookup("")}
                className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                ✕
              </button>
            )}
          </div>

          <div className="mt-2 text-sm">
            {digitsOnly.length === 0 && (
              <span className="text-gray-500">Enter 9 digits to search</span>
            )}
            {digitsOnly.length > 0 && digitsOnly.length < 9 && (
              <span className="text-gray-500">Keep typing…</span>
            )}
            {digitsOnly.length > 9 && (
              <span className="text-red-600">Too many digits (must be 9)</span>
            )}
            {searchLoading && <span className="text-gray-500">Searching…</span>}
            {!searchLoading && searchError && (
              <span className="text-red-600">{searchError}</span>
            )}
          </div>

          {/* Results (only when 9 digits) */}
          {digitsOnly.length === 9 && !searchLoading && !searchError && (
            <div className="mt-3">
              {searchResults.length === 0 ? (
                <div className="text-sm text-gray-500">No matches</div>
              ) : (
                <ul className="divide-y divide-gray-100 border rounded-md mt-2">
                  {searchResults.map((c) => {
                    const isBusiness = c.client.client_type === "business";
                    const idText = isBusiness
                      ? c.business?.ein
                        ? `EIN: ${formatEIN(c.business.ein)}`
                        : "EIN: —"
                      : c.person?.ssn
                        ? `SSN: ${formatSSN(c.person.ssn)}`
                        : "SSN: —";
                    return (
                      <li
                        key={c.id}
                        className="p-3 flex items-center justify-between"
                      >
                        <div>
                          <div className="font-medium text-gray-900">
                            {c.client.name}
                            <span className="ml-2 text-xs px-2 py-0.5 rounded-full border">
                              {isBusiness ? "Business" : "Person"}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {c.client.email} · {c.client.phone}
                          </div>
                          <div className="text-xs text-gray-500">{idText}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => navigate(`/client/${c.id}`)}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          Open
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Client Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {clientType === "person"
              ? "Personal Information"
              : "Business Information"}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-red-600 mb-2">
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>
            {clientType === "person" ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Spouse Name
                  </label>
                  <input
                    type="text"
                    name="spouse_name"
                    value={formData.spouse_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-red-600 mb-2">
                    Filing Status *
                  </label>
                  <select
                    name="filing_status"
                    value={formData.filing_status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select status</option>
                    {constantValues.personFilingStatusOptions.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-red-600 mb-2">
                    Filing Details *
                  </label>
                  <select
                    name="filing_details"
                    value={formData.filing_details}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select details</option>
                    {constantValues.filingDetailsOptions.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State Filing
                  </label>
                  <Select
                    name="state_filing"
                    isMulti
                    options={stateOptions}
                    value={stateOptions.filter((o) =>
                      formData.state_filing.includes(o.value),
                    )}
                    onChange={(selected) =>
                      setFormData((prev) => ({
                        ...prev,
                        state_filing: (selected || []).map((o) => o.value),
                      }))
                    }
                    closeMenuOnSelect={false}
                    placeholder="Select states"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    DBA
                  </label>
                  <input
                    type="text"
                    name="dba"
                    value={formData.dba}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-red-600 mb-2">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filing State
                  </label>
                  <Select
                    name="filing_state"
                    isMulti
                    options={stateOptions}
                    value={stateOptions.filter((o) =>
                      formData.filing_state.includes(o.value),
                    )}
                    onChange={(selected) =>
                      setFormData((prev) => ({
                        ...prev,
                        filing_state: (selected || []).map((o) => o.value),
                      }))
                    }
                    closeMenuOnSelect={false}
                    placeholder="Select states"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-red-600 mb-2">
                    Filing Status *
                  </label>
                  <select
                    name="filing_status"
                    value={formData.filing_status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select status</option>
                    {constantValues.businessfilingStatusOptions.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Common fields */}
            <div>
              <label className="block text-sm font-medium text-red-600 mb-2">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Secondary Email
              </label>
              <input
                type="email"
                name="secondary_email"
                value={formData.secondary_email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-red-600 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Phone Numbers
              </label>
              <input
                type="tel"
                name="additional_phones"
                value={formData.additional_phones}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Method of Contact
              </label>
              <select
                name="contact_method"
                value={formData.contact_method}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select method</option>
                {constantValues.contactMethodOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select status</option>
                {constantValues.statusOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City, State, Zip
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Foreign
              </label>
              <select
                name="international"
                value={formData.international}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select option</option>
                {constantValues.internationalOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {clientType === "person" ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-red-600 mb-2">
                    SSN *
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="ssn"
                    value={formData.ssn}
                    onChange={(e) => {
                      const digits = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 9);
                      setFormData((prev) => ({ ...prev, ssn: digits }));
                    }}
                    maxLength={9}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Spouse SSN
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="spouse_ssn"
                    value={formData.spouse_ssn}
                    onChange={(e) => {
                      const digits = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 9);
                      setFormData((prev) => ({ ...prev, spouse_ssn: digits }));
                    }}
                    maxLength={9}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-red-600 mb-2">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    name="date_birth"
                    value={formData.date_birth}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Spouse Date of Birth
                  </label>
                  <input
                    type="date"
                    name="spouse_date_birth"
                    value={formData.spouse_date_birth}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-red-600 mb-2">
                    EIN *
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="ein"
                    value={formData.ein}
                    onChange={(e) => {
                      const digits = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 9);
                      setFormData((prev) => ({ ...prev, ein: digits }));
                    }}
                    maxLength={9}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Incorporation Date
                  </label>
                  <input
                    type="date"
                    name="incorporation_date"
                    value={formData.incorporation_date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Incorporation State
                  </label>
                  <input
                    type="text"
                    name="incorporation_state"
                    value={formData.incorporation_state}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Accounting Method
                  </label>
                  <select
                    name="accounting_method"
                    value={formData.accounting_method}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select method</option>
                    {constantValues.accountingMethodOptions.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry
                  </label>
                  <input
                    type="text"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sales Representative
              </label>
              <AsyncCreatableSelect
                isClearable
                cacheOptions
                defaultOptions={salesRepOptions}
                loadOptions={debouncedLoadSales}
                components={{ Option: DeletableAsyncOption }}
                userPermission={userPermission}
                onDeleteOption={handleDeleteSalesRepOption}
                deletingOptionId={deletingSalesRepId}
                value={selectedSalesRepOption}
                onChange={(option) =>
                  setFormData((prev) => ({
                    ...prev,
                    sales_rep_id: option ? option.value : null,
                    sales_rep_name: option ? option.label : "",
                  }))
                }
                onCreateOption={async (name) => {
                  try {
                    const created = await clientService.createSalesRep(name);
                    const opt = { value: created.id, label: created.name };
                    setSalesRepDefaults((prev) => [...prev, opt]);
                    setFormData((prev) => ({
                      ...prev,
                      sales_rep_id: created.id,
                      sales_rep_name: created.name,
                    }));
                  } catch (e) {
                    console.error(e);
                    toast.error("Could not create Sales Rep");
                  }
                }}
                placeholder="Select or create a sales rep"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Manager
              </label>
              <AsyncCreatableSelect
                isClearable
                cacheOptions
                defaultOptions={clientManagerOptions}
                loadOptions={debouncedLoadManagers}
                components={{ Option: DeletableAsyncOption }}
                userPermission={userPermission}
                onDeleteOption={handleDeleteClientManagerOption}
                deletingOptionId={deletingClientManagerId}
                value={selectedClientManagerOption}
                onChange={(option) =>
                  setFormData((prev) => ({
                    ...prev,
                    client_manager_id: option ? option.value : null,
                    client_manager_name: option ? option.label : "",
                  }))
                }
                onCreateOption={async (name) => {
                  try {
                    const created =
                      await clientService.createClientManager(name);
                    const opt = { value: created.id, label: created.name };
                    setClientManagerDefaults((prev) => [...prev, opt]);
                    setFormData((prev) => ({
                      ...prev,
                      client_manager_id: created.id,
                      client_manager_name: created.name,
                    }));
                  } catch (e) {
                    console.error(e);
                    toast.error("Could not create Client Manager");
                  }
                }}
                placeholder="Select or create a client manager"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Language
              </label>
              <select
                name="language"
                value={formData.language}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select language</option>
                {constantValues.languageOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate("/clients")}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className="flex items-center px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="spinner mr-2">s</div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {loading ? "Creating..." : "Create Client"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateClient;
